/**
 * زخرفة اللوح الأيسر في شاشة الدخول — رسومٌ من الشعار ومن مجال الشركة نفسه (قرار المالك):
 * طيارة وسحابة ونوارس وسعف نخيل ونجمة بحر وكرة شاطئ، ومعها **تذكرة طيران وحقيبة سفر
 * وسرير فندق بنجومه وجواز سفر وكرة أرضية وخطّ رحلة منقّط** — لغة حجز الطيران والفنادق.
 *
 * **وبدون إزعاج**: كلّها أبيضُ بشفافية ٤٪ إلى ٩٪ على أزرق الهوية — تُحسّ ولا تُقرأ، فلا
 * تنازع الجملة ولا بطاقة موظف الشهر. ولا حركة ولا وميض.
 *
 * **ولماذا رسومٌ منفصلة لا لوحةٌ واحدة؟** اللوح يطول ويقصر بمقاس الشاشة، ولوحةٌ واحدة
 * بـ`slice` تُقصّ من أطرافها فتُقطع الطيارة نصفين (وهو ما ظهر فعلًا على شاشةٍ عريضة).
 * فكلُّ رسمٍ هنا مُثبَّت بنسبةٍ من اللوح ويحمل `viewBox` الخاص به — يصغر ويكبر ولا يُقصّ.
 *
 * ولا تمسّ الشعار الرسمي: الشعار يبقى ملفه الأصلي في مكوّن Logo، وهذه **إشارةٌ إليه**.
 * `aria-hidden` لأنها زخرفة خالصة: قارئ الشاشة لا يصف للمستخدم حقيبةً في شاشة دخول.
 */

type P = { className?: string; style?: React.CSSProperties };
const W = '#fff';

/** طيارة — ظلٌّ كطيارة الشعار، أنفها إلى أعلى اليمين */
function Plane({ className = '', style }: P) {
  return (
    <svg viewBox="0 0 100 100" className={className} style={style} fill={W}>
      <path d="M50 2c4 0 7 5 7 12v18l32 20v8l-32-11v20l12 9v6l-19-5-19 5v-6l12-9V49L11 60v-8l32-20V14c0-7 3-12 7-12z" />
    </svg>
  );
}

/** سحابة */
function Cloud({ className = '', style }: P) {
  return (
    <svg viewBox="0 0 200 110" className={className} style={style} fill={W}>
      <circle cx="56" cy="62" r="34" /><circle cx="104" cy="44" r="44" />
      <circle cx="152" cy="60" r="32" /><rect x="56" y="62" width="96" height="34" rx="17" />
    </svg>
  );
}

/** تذكرة طيران — نصفان يفصلهما خطُّ قصٍّ منقّط، كأي بطاقة صعود */
function Ticket({ className = '', style }: P) {
  return (
    <svg viewBox="0 0 140 76" className={className} style={style} fill="none" stroke={W} strokeWidth="3">
      <path d="M4 12a8 8 0 0 1 8-8h116a8 8 0 0 1 8 8v12a14 14 0 0 0 0 28v12a8 8 0 0 1-8 8H12a8 8 0 0 1-8-8V52a14 14 0 0 0 0-28V12z" />
      <path d="M100 8v60" strokeDasharray="5 7" strokeLinecap="round" />
      <path d="M22 28h56M22 42h40M22 54h28" strokeLinecap="round" />
    </svg>
  );
}

/** حقيبة سفر */
function Suitcase({ className = '', style }: P) {
  return (
    <svg viewBox="0 0 110 100" className={className} style={style} fill="none" stroke={W} strokeWidth="3">
      <path d="M40 22V14a8 8 0 0 1 8-8h14a8 8 0 0 1 8 8v8" strokeLinecap="round" />
      <rect x="8" y="22" width="94" height="62" rx="10" />
      <path d="M40 22v62M70 22v62" /><path d="M26 84v8M84 84v8" strokeLinecap="round" />
    </svg>
  );
}

/** سرير فندق بنجومه الخمس */
function Hotel({ className = '', style }: P) {
  return (
    <svg viewBox="0 0 150 96" className={className} style={style} fill="none" stroke={W} strokeWidth="3">
      <path d="M8 76V24M8 52h124a10 10 0 0 1 10 10v14M142 76v8M8 76v8" strokeLinecap="round" />
      <rect x="22" y="32" width="40" height="20" rx="8" />
      <g fill={W} stroke="none" transform="translate(74 6) scale(0.42)">
        {[0, 34, 68, 102, 136].map(x => (
          <polygon key={x} transform={`translate(${x} 0)`}
            points="15,0 19,10 30,10 21,17 25,28 15,21 5,28 9,17 0,10 11,10" />
        ))}
      </g>
    </svg>
  );
}

/** جواز سفر */
function Passport({ className = '', style }: P) {
  return (
    <svg viewBox="0 0 84 108" className={className} style={style} fill="none" stroke={W} strokeWidth="3">
      <rect x="4" y="4" width="76" height="100" rx="10" />
      <circle cx="42" cy="42" r="16" /><path d="M26 42h32M42 26c8 9 8 23 0 32M42 26c-8 9-8 23 0 32" />
      <path d="M26 78h32M32 90h20" strokeLinecap="round" />
    </svg>
  );
}

/** كرة أرضية */
function Globe({ className = '', style }: P) {
  return (
    <svg viewBox="0 0 100 100" className={className} style={style} fill="none" stroke={W} strokeWidth="3">
      <circle cx="50" cy="50" r="44" />
      <path d="M50 6c16 12 16 76 0 88M50 6C34 18 34 82 50 94" />
      <path d="M8 34h84M8 66h84M6 50h88" />
    </svg>
  );
}

/** خطّ رحلة منقّط بين محطّتين */
function Route({ className = '', style }: P) {
  return (
    <svg viewBox="0 0 260 90" className={className} style={style} fill="none" stroke={W} strokeWidth="3">
      <path d="M12 76C70 10 190 10 248 76" strokeDasharray="7 10" strokeLinecap="round" />
      <circle cx="12" cy="76" r="6" fill={W} stroke="none" />
      <circle cx="248" cy="76" r="6" fill={W} stroke="none" />
    </svg>
  );
}

/** نوارس */
function Gulls({ className = '', style }: P) {
  return (
    <svg viewBox="0 0 120 50" className={className} style={style} fill="none" stroke={W} strokeWidth="3" strokeLinecap="round">
      <path d="M6 22q11-11 22-1 11-10 22 1" /><path d="M64 40q8-8 16-1 8-7 16 1" />
    </svg>
  );
}

/** سعف نخيل */
function Palm({ className = '', style }: P) {
  return (
    <svg viewBox="0 0 150 150" className={className} style={style} fill="none" stroke={W} strokeWidth="3" strokeLinecap="round">
      <path d="M6 146q54-38 138-42" /><path d="M6 146q46-50 108-78" />
      <path d="M6 146q30-58 68-104" /><path d="M6 146q12-60 26-118" />
      <path d="M6 146q38-34 104-40" /><path d="M6 146q20-54 50-90" />
    </svg>
  );
}

/** نجمة بحر */
function Starfish({ className = '', style }: P) {
  return (
    <svg viewBox="0 0 100 100" className={className} style={style} fill={W}>
      <polygon points="50,3 61,38 97,38 68,59 79,93 50,72 21,93 32,59 3,38 39,38" />
    </svg>
  );
}

/** الحدّ المنقّط الذي يحيط دائرة الشعار */
function Stitch({ className = '', style }: P) {
  return (
    <svg viewBox="0 0 200 200" className={className} style={style} fill="none" stroke={W}
      strokeWidth="3" strokeDasharray="9 9" strokeLinecap="round">
      <circle cx="100" cy="100" r="94" />
    </svg>
  );
}

export function BrandMotifs({ className = '' }: { className?: string }) {
  return (
    <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
      {/* الشريط العلوي: سحابة وطيارة وخطّ رحلة ونوارس */}
      <Cloud className="absolute right-[6%] top-[4%] w-[26%] opacity-[0.06]" />
      <Plane className="absolute right-[10%] top-[1%] w-[13%] rotate-[32deg] opacity-[0.09]" />
      <Route className="absolute left-[4%] top-[9%] w-[34%] opacity-[0.055]" />
      <Gulls className="absolute left-[30%] top-[22%] w-[14%] opacity-[0.07]" />
      <Stitch className="absolute -right-[14%] top-[2%] w-[42%] opacity-[0.05]" />

      {/* الحافّة اليمنى: فندقٌ بنجومه، وجواز سفر */}
      <Hotel className="absolute right-[5%] top-[42%] w-[22%] opacity-[0.06]" />
      <Passport className="absolute right-[8%] bottom-[16%] w-[11%] -rotate-[10deg] opacity-[0.055]" />

      {/* الحافّة اليسرى: تذكرة طيران وحقيبة سفر */}
      <Ticket className="absolute left-[5%] top-[38%] w-[22%] -rotate-[8deg] opacity-[0.065]" />
      <Suitcase className="absolute left-[8%] bottom-[22%] w-[14%] rotate-[7deg] opacity-[0.055]" />

      {/* الشريط السفلي: نخيلٌ ونجمة بحر وكرة أرضية */}
      <Palm className="absolute -left-[3%] -bottom-[2%] w-[26%] opacity-[0.06]" />
      <Starfish className="absolute left-[38%] bottom-[6%] w-[9%] -rotate-[12deg] opacity-[0.05]" />
      <Globe className="absolute right-[30%] -bottom-[6%] w-[16%] opacity-[0.05]" />
      <Stitch className="absolute -left-[16%] -bottom-[18%] w-[38%] opacity-[0.045]" />
    </div>
  );
}
